import { ModelContract, ModuleDescription } from "../../shared/module-description";
import { IUserAuthContract } from "../../student/auth/model";
import { createEffect, createEvent, createStore, sample, combine } from "effector";
import { reset } from "patronum";
import { ICheckTaskRequestRecord, ISkipTaskRequestRecord, ITask, ICheckTaskV2RequestRecord } from "../../shared/entities/tasks";
import { ICurrentLessonContract } from "../../lessons/ui/current";
import { IEnrollmentsContract } from "../../enrollments/model";
import { toastShow } from "@features/notification/model";
import { ICustomError } from "@features/shared/entities/notification";
import { CourseDataApi } from "@features/shared/api/course-data.api";
import { TaskSkipped, TaskCompleted, TaskFailed } from "@features/shared/events";
import { $currentCourseId } from "@features/courses/ui/current";
import { $currentTopicId } from "../../topics/ui/current";

export const taskChecked = createEvent<ICheckTaskRequestRecord>();
export const taskSkipped = createEvent<ISkipTaskRequestRecord>();
export const taskReset = createEvent<ISkipTaskRequestRecord>();

export const tasksSkipped = createEvent<{ lessonId: number, tasksIds: string[] }>();
export const tasksReset = createEvent<{ lessonId: number, tasksIds: string[] }>();

// V2 flow for code-block based tasks (string id + explicit status)
export const taskStateReportedV2 = createEvent<ICheckTaskV2RequestRecord>();

export const getTasksByLessonIdFx = createEffect<{ lessonId: number }, ITask[]>();
const TASK_TYPE_LABELS: Record<number, "fillInTheBlanks" | "quiz" | "essay" | "promptEvaluation"> = {
    1: "fillInTheBlanks",
    2: "quiz",
    3: "essay",
    4: "promptEvaluation",
};

const getTaskTypeById = (id: number): "fillInTheBlanks" | "quiz" | "essay" | "promptEvaluation" | "unknown" => {
    return TASK_TYPE_LABELS[id] ?? "unknown";
};

export const $tasks = createStore<ITask[]>([]).on(getTasksByLessonIdFx.doneData, (_, p) => p);

getTasksByLessonIdFx.use(async ({ lessonId }: { lessonId: number }) => {
    const response = await CourseDataApi.getTasksByLessonId(lessonId);
    return response
});

export const taskCheckedFx = createEffect<ICheckTaskRequestRecord, { status: string }>(async (data) => {
    const response = await CourseDataApi.checkTask(data);
    return response as { status: string };
});

export const taskSkippedFx = createEffect(async (data: ISkipTaskRequestRecord) => {
    const response = await CourseDataApi.skipTask(data);
    return response;
});

export const checkTaskV2Fx = createEffect<ICheckTaskV2RequestRecord, unknown>(async (data) => {
    return await CourseDataApi.checkTaskV2(data);
});

export const trackTaskSkippedFx = createEffect(({ lessonId, taskId, courseId, topicId, task_type_id }: { lessonId: number; taskId: string; courseId: number; topicId: number; task_type_id: number }) => {
    TaskSkipped(courseId, topicId, lessonId, taskId, getTaskTypeById(task_type_id));
});

const trackTaskCheckedFx = createEffect(({ lessonId, taskId, status, courseId, topicId, task_type_id }: { lessonId: number; taskId: string; status: string; courseId: number; topicId: number; task_type_id: number }) => {
    if (status === "completed") {
        TaskCompleted(courseId, topicId, lessonId, taskId, getTaskTypeById(task_type_id));
    } else {
        TaskFailed(courseId, topicId, lessonId, taskId, getTaskTypeById(task_type_id));
    }
});

const skipTasksListFx = createEffect(async (data: { lessonId: number; tasksIds: string[]; courseId: number; topicId: number }) => {
    await CourseDataApi.skipTaskList(data)
});

const resetTasksListFx = createEffect(async (data: { lessonId: number; tasksIds: string[]; courseId: number; topicId: number }) => {
    await CourseDataApi.resetTaskList(data)
})

export const taskResetFx = createEffect(async (data: ISkipTaskRequestRecord) => {
    const response = await CourseDataApi.resetTask(data)
    return response;
});

const $taskContext = combine({
    courseId: $currentCourseId,
    topicId: $currentTopicId,
    tasks: $tasks,
});

sample({
    source: $taskContext,
    clock: taskCheckedFx.done,
    filter: ({ courseId, topicId }) => courseId !== null && topicId !== null,
    fn: ({ courseId, topicId, tasks }, { params, result }) => {
        const task = tasks.find(t => t.id === params.taskId);
        return {
            lessonId: params.lessonId,
            taskId: params.taskId,
            status: result.status,
            courseId: courseId as number,
            topicId: topicId as number,
            task_type_id: task?.task_type_id ?? -1,
        };
    },
    target: trackTaskCheckedFx
});

sample({
    source: {
        courseId: $currentCourseId,
        topicId: $currentTopicId,
        tasks: $tasks,
    },
    clock: taskSkipped,
    filter: ({ courseId, topicId }) => courseId !== null && topicId !== null,
    fn: ({ courseId, topicId, tasks }, { lessonId, taskId }): { courseId: number; topicId: number; lessonId: number; taskId: string; task_type_id: number; } => {
        const task = tasks.find((t) => t.id === taskId);
        return {
            courseId: courseId as number,
            topicId: topicId as number,
            lessonId,
            taskId,
            task_type_id: task?.task_type_id ?? -1,
        };
    },
    target: trackTaskSkippedFx,
});

sample({
    source: {
        courseId: $currentCourseId,
        topicId: $currentTopicId,
        tasks: $tasks,
    },
    clock: tasksSkipped,
    filter: ({ courseId, topicId }) => courseId !== null && topicId !== null,
    fn: ({ courseId, topicId, tasks }, { lessonId, tasksIds }) => {
        tasksIds.forEach((id) => {
            const task = tasks.find((t) => t.id === id);
            const type = getTaskTypeById(task?.task_type_id ?? -1);
            TaskSkipped(courseId as number, topicId as number, lessonId, id, type);
        });

        return {
            courseId: courseId as number,
            topicId: topicId as number,
            lessonId,
            tasksIds,
        };
    },
    target: skipTasksListFx,
});

sample({
    source: {
        courseId: $currentCourseId,
        topicId: $currentTopicId,
    },
    clock: tasksReset,
    filter: ({ courseId, topicId }) => courseId !== null && topicId !== null,
    fn: ({ courseId, topicId }, { lessonId, tasksIds }) => ({
        courseId: courseId as number,
        topicId: topicId as number,
        lessonId,
        tasksIds,
    }),
    target: resetTasksListFx,
});

sample({
    clock: taskSkipped,
    target: taskSkippedFx
});

sample({
    clock: taskChecked,
    target: taskCheckedFx,
});

sample({
    clock: taskReset,
    target: taskResetFx,
});

// Wire V2 event to effect
sample({
    clock: taskStateReportedV2,
    target: checkTaskV2Fx
});

sample({
    clock: [getTasksByLessonIdFx.fail, taskSkippedFx.fail, taskCheckedFx.fail, taskResetFx.fail],
    filter: ({ error }: { error: ICustomError }) => error?.error?.title !== null,
    fn: ({ error }: { error: ICustomError }) => error?.error,
    target: toastShow
});

const contract = new ModelContract(
    {
        $tasks
    },
    {
        taskChecked,
        taskSkipped,
        taskReset,
        tasksSkipped,
        tasksReset,
        taskStateReportedV2
    },
    // Effects are exposed in both slots: consumers read `effects`, the model tests override via `__overridesForTests`.
    { getTasksByLessonIdFx, taskCheckedFx, taskSkippedFx, taskResetFx, checkTaskV2Fx },
    { getTasksByLessonIdFx, taskCheckedFx, taskSkippedFx, taskResetFx, checkTaskV2Fx }
)

export type ITasksContract = typeof contract;

export class TasksModule extends ModuleDescription<ITasksContract> {
    constructor(
        public userAuthModule: IUserAuthContract,
        public currentLessonModule: ICurrentLessonContract,
        public enrollmentsModule: IEnrollmentsContract,
    ) {
        super();
    }

    getDefaultContract() {
        return contract;
    }

    run(): void {
        sample({
            clock: this.currentLessonModule.events.currentLessonIdChanged,
            source: {
                lessonId: this.currentLessonModule.stores.$currentLessonId,
                courseId: $currentCourseId,
                topicId: $currentTopicId,
            },
            filter: ({ lessonId, courseId, topicId }) => lessonId !== null && courseId !== null && topicId !== null,
            fn: ({ lessonId, courseId, topicId }) => ({
                lessonId: lessonId as number,
                courseId: courseId as number,
                topicId: topicId as number,
            }),
            target: getTasksByLessonIdFx
        });

        sample({
            source: this.userAuthModule.stores.$student,
            clock: [
                taskCheckedFx.done, taskResetFx.done, taskSkippedFx.done,
                skipTasksListFx.done, resetTasksListFx.done,
                checkTaskV2Fx.done
            ],
            filter: (student) => student !== null,
            target: this.enrollmentsModule.events.enrollTasksLoaded
        });

        reset({
            clock: this.userAuthModule.events.logoutStudent,
            target: $tasks
        })
    }
}
